import { useMemo, type ReactNode } from 'react';
import { Card, CardContent, Typography, Stack, Box, Chip } from '@mui/material';
import { WorkerTypeChip } from '@billda/shared-ui';
import { FinancialSummaryRow } from './FinancialSummaryRow';
import { formatCurrency } from '../../../shared/utils/currency';
import type { PotDetailsDisplayModel } from '../types/potDetails.types';

interface CondensedPotSummaryProps {
  potDetails: PotDetailsDisplayModel;
  showDeductions?: boolean;
  renderAfterFinancials?: ReactNode;
}

export function CondensedPotSummary({
  potDetails,
  showDeductions = true,
  renderAfterFinancials,
}: CondensedPotSummaryProps) {
  // Calculate fixed rate and ad-hoc totals
  const { fixedRateTotal, adHocTotal, fixedRateGangMembers, adHocWorkers } =
    useMemo(() => {
      const gangMembers = potDetails.fixedRateWorkers.filter(
        w => w.isGangMember
      );
      const adhoc = potDetails.fixedRateWorkers.filter(w => !w.isGangMember);
      return {
        fixedRateGangMembers: gangMembers,
        adHocWorkers: adhoc,
        fixedRateTotal: gangMembers.reduce((sum, w) => sum + w.totalAmount, 0),
        adHocTotal: adhoc.reduce((sum, w) => sum + w.totalAmount, 0),
      };
    }, [potDetails.fixedRateWorkers]);

  // Combine all workers for display
  const allWorkers = useMemo(() => {
    const workers: Array<{
      userId: string;
      userName: string;
      type: 'percentage' | 'fixedRate' | 'adhoc';
      amount: number;
      percentage?: number;
    }> = [];

    // Add percentage split workers
    potDetails.gangMemberSplits.forEach(member => {
      workers.push({
        userId: member.userId,
        userName: member.userName,
        type: 'percentage',
        amount: member.amount,
        percentage: member.percentage,
      });
    });

    // Add fixed rate gang members
    fixedRateGangMembers.forEach(worker => {
      workers.push({
        userId: worker.userId,
        userName: worker.userName,
        type: 'fixedRate',
        amount: worker.totalAmount,
      });
    });

    // Add ad-hoc workers
    adHocWorkers.forEach(worker => {
      workers.push({
        userId: worker.userId,
        userName: worker.userName,
        type: 'adhoc',
        amount: worker.totalAmount,
      });
    });

    return workers;
  }, [potDetails.gangMemberSplits, fixedRateGangMembers, adHocWorkers]);

  return (
    <Stack spacing={2}>
      {/* Financial Summary Card */}
      {potDetails.financials && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Financial Summary
            </Typography>
            <Stack spacing={1.5}>
              <FinancialSummaryRow
                label="Gross Amount"
                value={formatCurrency(potDetails.financials.grossAmount)}
              />
              {fixedRateTotal > 0 && (
                <FinancialSummaryRow
                  label="Fixed Rate Workers"
                  value={`-${formatCurrency(fixedRateTotal)}`}
                  valueColor="error"
                />
              )}
              {adHocTotal > 0 && (
                <FinancialSummaryRow
                  label="Ad-hoc Workers"
                  value={`-${formatCurrency(adHocTotal)}`}
                  valueColor="error"
                />
              )}
              {potDetails.financials.totalDeductions > 0 && (
                <FinancialSummaryRow
                  label="Deductions"
                  value={`-${formatCurrency(potDetails.financials.totalDeductions)}`}
                  valueColor="error"
                />
              )}
              <FinancialSummaryRow
                label="Net Amount"
                value={formatCurrency(potDetails.financials.netAmount)}
                valueColor="primary"
                emphasis
                dividerAbove
              />
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Content to render after financials */}
      {renderAfterFinancials}

      {/* Workers Card - only show if there are workers */}
      {allWorkers.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Workers ({allWorkers.length})
            </Typography>
            <Stack spacing={1}>
              {allWorkers.map(worker => (
                <Box
                  key={worker.userId}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">{worker.userName}</Typography>
                    <WorkerTypeChip type={worker.type} />
                    {worker.percentage !== undefined && (
                      <Typography variant="caption" color="text.secondary">
                        ({Math.round(worker.percentage * 100)}%)
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" fontWeight="medium">
                    {formatCurrency(worker.amount)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Deductions Card - only show if there are deductions and showDeductions is true */}
      {showDeductions && potDetails.deductions.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Deductions (
              {
                potDetails.deductions.filter(d => d.status !== 'Cancelled')
                  .length
              }
              )
            </Typography>
            <Stack spacing={1}>
              {potDetails.deductions.map(deduction => (
                <Box
                  key={deduction.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ flex: 1, mr: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                      }}
                    >
                      {deduction.description}
                    </Typography>
                    {deduction.uniqueReference && (
                      <Typography variant="body2">
                        {deduction.uniqueReference}
                      </Typography>
                    )}
                    {deduction.plotReference && (
                      <Typography variant="caption" color="text.secondary">
                        Plot: {deduction.plotReference}
                      </Typography>
                    )}
                  </Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ flexShrink: 0 }}
                  >
                    {deduction.status === 'Cancelled' && (
                      <Chip
                        label="Cancelled"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{
                        whiteSpace: 'nowrap',
                        color:
                          deduction.status === 'Cancelled'
                            ? 'text.disabled'
                            : undefined,
                        textDecoration:
                          deduction.status === 'Cancelled'
                            ? 'line-through'
                            : 'none',
                      }}
                    >
                      {formatCurrency(deduction.amount)}
                    </Typography>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
